import React from "react";
import { ChevronLeft, ChevronRight, BookOpen, Download, Phone } from "lucide-react";
import { Loader2 } from "lucide-react";
import { REALTOR } from "../lib/brand";
import { ICON_SIZE } from "../lib/constants";

export function ReaderHeader({
    label,
    progressPercent,
    downloading,
    onOpenToc,
    onDownload,
}) {
    return (
        <header className="sticky top-0 z-30 bg-cream/90 backdrop-blur-md border-b border-hairline">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between gap-3">
                <button
                    onClick={onOpenToc}
                    data-testid="toc-open-btn"
                    className="flex items-center gap-2 text-navy text-xs tracking-[0.18em] uppercase font-semibold hover:text-coral transition-colors"
                >
                    <BookOpen size={ICON_SIZE.INLINE_LG} />
                    <span className="hidden sm:inline">Contents</span>
                </button>
                <div className="text-xs tracking-[0.18em] uppercase text-muted2 truncate">
                    <span className="text-navy">{label}</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                    <a
                        href={`tel:${REALTOR.phoneTel}`}
                        className="hidden md:inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-semibold text-navy hover:text-coral transition-colors"
                    >
                        <Phone size={ICON_SIZE.INLINE_MD} />
                        {REALTOR.phone}
                    </a>
                    <button
                        onClick={onDownload}
                        disabled={downloading}
                        data-testid="header-download-btn"
                        className="inline-flex items-center gap-2 bg-navy text-cream py-2 px-4 rounded-sm font-sans text-[10px] sm:text-xs font-semibold tracking-[0.18em] uppercase transition-all hover:bg-navy-hover hover:-translate-y-0.5 disabled:opacity-60"
                    >
                        {downloading ? (
                            <Loader2 size={ICON_SIZE.INLINE_MD} className="animate-spin" />
                        ) : (
                            <Download size={ICON_SIZE.INLINE_MD} />
                        )}
                        <span className="hidden sm:inline">PDF</span>
                    </button>
                </div>
            </div>
            <div className="h-0.5 bg-sand">
                <div
                    className="h-full bg-navy transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                />
            </div>
        </header>
    );
}

export function ReaderFooterNav({ pageNum, totalPages, onPrev, onNext }) {
    const isFirst = pageNum === 1;
    const isLast = pageNum === totalPages;
    return (
        <div className="sticky bottom-0 z-20 bg-cream/90 backdrop-blur-md border-t border-hairline">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-3">
                <button
                    onClick={onPrev}
                    disabled={isFirst}
                    data-testid="page-prev-btn"
                    className="group inline-flex items-center gap-2 text-navy text-xs tracking-[0.18em] uppercase font-semibold hover:text-coral transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    <ChevronLeft
                        size={ICON_SIZE.INLINE_LG}
                        className="transition-transform group-hover:-translate-x-1"
                    />
                    <span className="hidden sm:inline">Previous</span>
                </button>
                <div className="text-[10px] sm:text-xs tracking-[0.22em] uppercase text-muted2">
                    {pageNum} <span className="mx-2">/</span> {totalPages}
                </div>
                <button
                    onClick={onNext}
                    disabled={isLast}
                    data-testid="page-next-btn"
                    className="group inline-flex items-center gap-2 bg-navy text-cream py-2.5 px-5 rounded-sm text-xs tracking-[0.18em] uppercase font-semibold transition-all hover:bg-navy-hover hover:-translate-y-0.5 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                    <span>Next</span>
                    <ChevronRight
                        size={ICON_SIZE.INLINE_LG}
                        className="transition-transform group-hover:translate-x-1"
                    />
                </button>
            </div>
        </div>
    );
}
